import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Star } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PricingPage = () => {
  const [packages, setPackages] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const packagesRes = await axios.get(`${API}/packages`);
        const servicesRes = await axios.get(`${API}/services`);
        setPackages(packagesRes.data.packages);
        setServices(servicesRes.data.services);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="pricing-page" className="bg-[#050505]">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-[#D4AF37] text-sm uppercase tracking-widest mb-4 block">
              Áraink
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl text-[#E5E5E5] mb-6">
              ÁTLÁTHATÓ ÁRAZÁS
              <span className="block text-[#D4AF37]">REJTETT KÖLTSÉGEK NÉLKÜL</span>
            </h1>
            <p className="text-[#A3A3A3] text-lg leading-relaxed">
              Válasszon csomagjaink közül, vagy kérjen egyedi ajánlatot. 
              Minden ár tartalmazza az anyagköltséget és a munkadíjat.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Packages */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl text-[#E5E5E5] mb-4">CSOMAGAJÁNLATOK</h2>
            <p className="text-[#A3A3A3]">Válassza ki az Önnek megfelelő csomagot</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {packages.map((pkg, index) => (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative bg-[#121212] border ${
                  pkg.popular ? "border-[#D4AF37]" : "border-[#2A2A2A]"
                } p-8 flex flex-col`}
                data-testid={`package-${pkg.id}`}
              >
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-gradient-to-r from-[#D4AF37] to-[#FFE57F] px-4 py-1 text-xs font-bold uppercase tracking-widest text-black flex items-center gap-1">
                      <Star size={12} /> Legnépszerűbb
                    </span>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="font-heading text-2xl text-[#E5E5E5] mb-2">{pkg.name}</h3>
                  <p className="text-[#A3A3A3] text-sm">{pkg.description}</p>
                </div>

                <div className="mb-8">
                  <span className="font-heading text-4xl text-[#D4AF37]">
                    {pkg.price.toLocaleString()}
                  </span>
                  <span className="text-[#A3A3A3]"> Ft</span>
                </div>

                <ul className="space-y-3 mb-8 flex-grow">
                  {pkg.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check size={18} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                      <span className="text-[#A3A3A3] text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link to="/idopontfoglalas">
                  <Button
                    data-testid={`book-package-${pkg.id}`}
                    className={`w-full py-4 uppercase tracking-widest rounded-sm font-bold ${
                      pkg.popular
                        ? "bg-[#D4AF37] text-black hover:bg-[#B5952F]"
                        : "bg-transparent border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black"
                    }`}
                  >
                    Foglalás
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Individual Services */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl text-[#E5E5E5] mb-4">EGYEDI SZOLGÁLTATÁSOK</h2>
            <p className="text-[#A3A3A3]">Válasszon külön szolgáltatásokat igény szerint</p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-[#121212] border border-[#2A2A2A] divide-y divide-[#2A2A2A]">
              {services.map((service, index) => (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:bg-[#1E1E1E] transition-colors"
                  data-testid={`service-price-${service.id}`}
                >
                  <div className="flex-grow">
                    <h4 className="font-heading text-lg text-[#E5E5E5] mb-1">{service.name}</h4>
                    <p className="text-[#666] text-sm">{service.duration}</p>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <span className="text-[#D4AF37] font-heading text-xl">
                        {service.price_from.toLocaleString()} Ft
                      </span>
                      <span className="text-[#666] text-sm block">-tól</span>
                    </div>
                    <Link to="/idopontfoglalas">
                      <Button
                        size="sm"
                        className="bg-[#D4AF37] text-black hover:bg-[#B5952F] px-4 py-2 rounded-sm"
                      >
                        <ArrowRight size={16} />
                      </Button>
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Note */}
      <section className="py-16 bg-[#050505] border-t border-[#2A2A2A]">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-[#A3A3A3] text-sm leading-relaxed">
              * Az árak tájékoztató jellegűek és az autó méretétől, állapotától függően változhatnak. 
              Kérjük, vegye fel velünk a kapcsolatot pontos árajánlatért. 
              Minden ár tartalmazza az ÁFÁ-t.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#121212]">
        <div className="container mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl text-[#E5E5E5] mb-6">
            EGYEDI AJÁNLATOT SZERETNE?
          </h2>
          <p className="text-[#A3A3A3] mb-8 max-w-xl mx-auto">
            Vegye fel velünk a kapcsolatot, és személyre szabott árajánlatot készítünk az Ön igényei alapján.
          </p>
          <Link to="/kapcsolat">
            <Button
              data-testid="contact-cta-btn"
              className="bg-[#D4AF37] text-black font-bold uppercase tracking-widest hover:bg-[#B5952F] px-8 py-4 rounded-sm"
            >
              Kapcsolat
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
